import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const NavigationSearch = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const navigationItems = [
    { label: 'Dashboard', path: '/role-based-dashboard', icon: 'LayoutDashboard', description: 'View your dashboard overview' },
    { label: 'Profile Management', path: '/student-profile-management', icon: 'UserCircle', description: 'Manage your student profile' },
    { label: 'Job Search', path: '/job-search-application', icon: 'Search', description: 'Search and apply for jobs' },
    { label: 'Post Job', path: '/job-posting', icon: 'PlusCircle', description: 'Create new job posting' },
    { label: 'Candidates', path: '/candidate-management', icon: 'Users', description: 'Manage job candidates' },
    { label: 'Institutions', path: '/institution-management', icon: 'Building2', description: 'Manage institutions' },
    { label: 'Admin Controls', path: '/admin-controls', icon: 'Shield', description: 'Access admin settings' },
    { label: 'Analytics', path: '/analytics', icon: 'BarChart3', description: 'View analytics and reports' },
    { label: 'Notifications', path: '/notifications', icon: 'Bell', description: 'Check your notifications' },
    { label: 'Support', path: '/support', icon: 'HelpCircle', description: 'Get help and support' }
  ];

  const filteredItems = navigationItems?.filter(item =>
    item?.label?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    item?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  const handleKeyDown = (e) => {
    if (e?.key === 'ArrowDown') {
      e?.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filteredItems?.length);
    } else if (e?.key === 'ArrowUp') {
      e?.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filteredItems?.length) % filteredItems?.length);
    } else if (e?.key === 'Enter' && filteredItems?.[selectedIndex]) {
      handleNavigate(filteredItems?.[selectedIndex]?.path);
    } else if (e?.key === 'Escape') {
      onClose();
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
    setSearchQuery('');
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-150 transition-opacity"
        onClick={onClose}
      />
      <div className="fixed top-20 left-1/2 transform -translate-x-1/2 w-full max-w-2xl z-150 px-4">
        <div className="bg-card border border-border rounded-lg shadow-2xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <Icon name="Search" size={20} color="var(--color-muted-foreground)" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search pages, features, or navigate..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent text-card-foreground placeholder-muted-foreground focus:outline-none text-base"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs bg-muted rounded border border-border">
              <span>ESC</span>
            </kbd>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {filteredItems?.length > 0 ? (
              <ul className="py-2">
                {filteredItems?.map((item, index) => (
                  <li key={item?.path}>
                    <button
                      onClick={() => handleNavigate(item?.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 transition-smooth ${
                        index === selectedIndex
                          ? 'bg-primary/10 text-primary' :'text-card-foreground hover:bg-muted'
                      }`}
                    >
                      <div className={`p-2 rounded-md ${
                        index === selectedIndex ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        <Icon
                          name={item?.icon}
                          size={20}
                          color={index === selectedIndex ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">{item?.label}</div>
                        <div className="text-xs text-muted-foreground">{item?.description}</div>
                      </div>
                      {index === selectedIndex && (
                        <Icon name="CornerDownLeft" size={16} color="var(--color-muted-foreground)" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="py-12 text-center">
                <Icon name="SearchX" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
                <p className="text-muted-foreground">No results found for "{searchQuery}"</p>
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-border bg-muted/30">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-background rounded border border-border">↑</kbd>
                  <kbd className="px-2 py-1 bg-background rounded border border-border">↓</kbd>
                  <span>Navigate</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-background rounded border border-border">Enter</kbd>
                  <span>Select</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Zap" size={12} />
                <span>Quick Navigation</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavigationSearch;